// src/components/Prospecting/TradeShows/UserTradeShows.jsx

import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Button,
  Chip,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
} from "@mui/material";

import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import SearchIcon from "@mui/icons-material/Search";

import BrandPagination from "../../../layouts/Pagination";
import BackButton from "../../../shared/components/BackButton";
import SearchBar from "../../../shared/components/SearchBar";
import { SELECT_MENU_PROPS } from "../../../theme/filterstyles";
import { COUNTRY_MAP } from "../../../utils/Countries";
import { US_STATE_MAP, CANADA_PROVINCE_MAP } from "../../../utils/States";
import api from "../../../services/api/axios";
import {
  downloadCsvFile,
  normalizeTradeShowCountryCode,
  normalizeTradeShowUrl,
} from "./TradeShowsHelpers";
import { ACTIONS_HEADER_BTN_SX, ACTION_MENU_PAPER_SX } from "../../../theme/buttonstyles";

/* =========================
   CONSTANTS
========================= */

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PAGE_BG = "#f7f9fc";
const ROWS_PER_PAGE = 50;
const FILTER_MENU_WIDTH = 360;
const FILTER_MENU_MAX_WIDTH = "calc(100vw - 32px)";

const MONTHS = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
  background: "#ffffff",
  overflow: "hidden",
};


const HEAD_CELL_SX = {
  backgroundColor: BRAND_BLUE,
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};

const DATA_CELL_SX = {
  fontSize: "0.875rem",
  color: "#344863",
  borderBottom: "1px solid #edf0f5",
  py: 0.875,
};

const HEADER_TRIGGER_SX = {
  minWidth: 108,
  minHeight: 28,
  px: 1.5,
  py: 0.35,
  fontWeight: 700,
  fontSize: "0.72rem",
  color: BRAND_BLUE,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  borderRadius: 1.5,
  border: "1.5px solid rgba(255,255,255,0.35)",
  backgroundColor: "rgba(255,255,255,0.92)",
  "&:hover": { backgroundColor: "#ffffff", borderColor: "rgba(255,255,255,0.8)" },
};

const SEARCH_FIELD_SX = {
  px: 1,
  pt: 0.75,
  pb: 0.25,
  backgroundColor: "white",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const FILTER_SEARCH_INPUT_SX = {
  "& .MuiInputBase-root": { fontSize: "0.875rem", backgroundColor: "#ffffff" },
  "& .MuiInputBase-input": { py: 0.75, color: BRAND_BLUE, fontWeight: 700 },
  "& input": { color: `${BRAND_BLUE} !important`, WebkitTextFillColor: BRAND_BLUE, caretColor: BRAND_BLUE },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};

const FILTER_SECTION_LABEL_SX = {
  px: 2, pt: 1.2, pb: 0.4,
  color: BRAND_BLUE,
  fontSize: "0.76rem",
  fontWeight: 800,
  textTransform: "uppercase",
};

function displayCount(value) {
  if (value === null || value === undefined || value === "") return "—";
  return value;
}

function cleanDisplay(v) {
  return v ? String(v).trim() : "";
}

function toTitleCase(str) {
  return String(str).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function countryLabel(option) {
  return COUNTRY_MAP[option] || toTitleCase(option);
}

/* =========================
   COMPONENT
========================= */

export default function UserTradeShows() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [selectedState, setSelectedState] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState([]);
  const [selectedYear, setSelectedYear] = useState([]);

  // Actions menu state
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  // Filter menu state
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  /* =========================
     DATA FETCH
  ========================= */

  const fetchTradeShows = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/user-trade-shows");
      const parsed = (Array.isArray(data) ? data : []).map((r) => ({
        ...r,
        country_code: normalizeTradeShowCountryCode(r.new_country || r.country || ""),
        new_country: r.new_country || r.country || "",
        new_industry: r.new_industry || r.industry || "",
        start_date_raw: r.start_date_raw || r.start_date || "",
      }));
      setRows(parsed);
      setSelectedIds(new Set());
      setPage(1);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load saved trade shows");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTradeShows(); }, []);

  /* =========================
     FILTER OPTIONS
  ========================= */

  const normalizedFilterSearch = filterSearch.trim().toLowerCase();
  const filterOption = useCallback(
    (label) => !normalizedFilterSearch || label.toLowerCase().includes(normalizedFilterSearch),
    [normalizedFilterSearch],
  );

  const countryOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const c = normalizeTradeShowCountryCode(cleanDisplay(r.new_country) || cleanDisplay(r.country));
      if (c) set.add(c);
    });
    return [...set].sort();
  }, [rows]);

  const stateOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const rowCountry = normalizeTradeShowCountryCode(cleanDisplay(r.new_country) || cleanDisplay(r.country));
      if (!selectedCountry.includes(rowCountry)) return;
      const rawState = cleanDisplay(r.state);
      if (!rawState) return;
      const code = rawState.toUpperCase();
      const isUS = Boolean(US_STATE_MAP[code]);
      const isCA = Boolean(CANADA_PROVINCE_MAP[code]);
      if (!isUS && !isCA) return;
      map.set(code, { code, label: US_STATE_MAP[code] || CANADA_PROVINCE_MAP[code] || rawState, group: isUS ? "United States" : "Canada" });
    });
    return [...map.values()].sort((a, b) => a.group !== b.group ? a.group.localeCompare(b.group) : a.label.localeCompare(b.label));
  }, [rows, selectedCountry]);

  const industryOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const ind = cleanDisplay(r.new_industry) || cleanDisplay(r.industry);
      if (ind) set.add(ind);
    });
    return [...set].sort();
  }, [rows]);

  const yearOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const raw = r.start_date_raw;
      if (!raw) return;
      const d = new Date(raw);
      if (!isNaN(d)) set.add(String(d.getFullYear()));
    });
    return [...set].sort((a, b) => Number(b) - Number(a));
  }, [rows]);

  const filteredCountryOptions = useMemo(
    () => countryOptions.filter((o) => filterOption(countryLabel(o))),
    [countryOptions, filterOption],
  );
  const filteredStateOptions = useMemo(
    () => stateOptions.filter((o) => filterOption(`${o.label} ${o.group}`)),
    [stateOptions, filterOption],
  );
  const filteredIndustryOptions = useMemo(
    () => industryOptions.filter((o) => filterOption(o)),
    [industryOptions, filterOption],
  );
  const filteredMonthOptions = useMemo(
    () => MONTHS.filter((o) => filterOption(o.label)),
    [filterOption],
  );
  const filteredYearOptions = useMemo(
    () => yearOptions.filter((o) => filterOption(o)),
    [yearOptions, filterOption],
  );

  /* =========================
     FILTER MENU HELPERS
  ========================= */

  const openFilterMenu = useCallback((event, filterKey) => {
    setFilterAnchor(event.currentTarget);
    setActiveFilter(filterKey);
    setFilterSearch("");
  }, []);

  const closeFilterMenu = useCallback(() => {
    setFilterAnchor(null);
    setActiveFilter(null);
    setFilterSearch("");
  }, []);

  const toggleStringValue = useCallback((values, value) => {
    return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
  }, []);

  const renderFilterTrigger = (label, filterKey, active = false, minWidth = 108) => (
    <Stack direction="row" alignItems="center" justifyContent="center">
      <Button
        variant="text"
        onClick={(e) => openFilterMenu(e, filterKey)}
        sx={{
          ...HEADER_TRIGGER_SX,
          minWidth,
          color: active ? BRAND_GREEN : BRAND_BLUE,
          border: active ? `1.5px solid ${BRAND_GREEN}` : HEADER_TRIGGER_SX.border,
          backgroundColor: "#ffffff",
          "&:hover": { backgroundColor: "#ffffff", borderColor: active ? BRAND_GREEN : "rgba(255,255,255,0.8)" },
        }}
        endIcon={<ArrowDropDownIcon sx={{ fontSize: "1.15rem" }} />}
      >
        {label}
      </Button>
    </Stack>
  );

  const renderSearchField = (placeholder) => (
    <Box sx={SEARCH_FIELD_SX}>
      <TextField
        autoFocus
        fullWidth
        size="small"
        value={filterSearch}
        onChange={(e) => setFilterSearch(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        placeholder={placeholder}
        sx={FILTER_SEARCH_INPUT_SX}
        slotProps={{ input: { startAdornment: <SearchIcon sx={{ mr: 1, color: BRAND_BLUE }} /> } }}
      />
    </Box>
  );

  const renderOptionItem = ({ key, label, checked, onClick, disabled = false }) => (
    <MenuItem key={key} selected={checked} disabled={disabled} onClick={onClick}>
      {label}
    </MenuItem>
  );

  const commonMenuProps = {
    open: Boolean(filterAnchor),
    anchorEl: filterAnchor,
    onClose: closeFilterMenu,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    marginThreshold: 16,
    MenuListProps: {
      dense: true,
      sx: { py: 0, maxHeight: 520, width: FILTER_MENU_WIDTH, maxWidth: FILTER_MENU_MAX_WIDTH, boxSizing: "border-box", overflowX: "hidden" },
    },
    ...SELECT_MENU_PROPS,
    PaperProps: {
      ...SELECT_MENU_PROPS.PaperProps,
      sx: {
        ...(SELECT_MENU_PROPS.PaperProps?.sx || {}),
        width: FILTER_MENU_WIDTH,
        maxWidth: FILTER_MENU_MAX_WIDTH,
        boxSizing: "border-box",
        maxHeight: 560,
        overflowX: "hidden",
        overflowY: "auto",
        py: 0,
      },
    },
  };

  const renderFilterMenu = () => {
    if (!activeFilter) return null;

    if (activeFilter === "dates") {
      return (
        <Menu {...commonMenuProps}>
          {renderSearchField("Search month or year")}
          <MenuItem selected={!selectedMonth.length && !selectedYear.length} onClick={() => { setSelectedMonth([]); setSelectedYear([]); }}>
            All Dates
          </MenuItem>
          <Typography sx={FILTER_SECTION_LABEL_SX}>Month</Typography>
          {filteredMonthOptions.map((o) => renderOptionItem({
            key: `month-${o.value}`,
            label: o.label,
            checked: selectedMonth.includes(o.value),
            onClick: () => setSelectedMonth(toggleStringValue(selectedMonth, o.value)),
          }))}
          <Typography sx={FILTER_SECTION_LABEL_SX}>Year</Typography>
          {filteredYearOptions.map((o) => renderOptionItem({
            key: `year-${o}`,
            label: o,
            checked: selectedYear.includes(o),
            onClick: () => setSelectedYear(toggleStringValue(selectedYear, o)),
          }))}
        </Menu>
      );
    }

    if (activeFilter === "location") {
      const northAmericaSelected = selectedCountry.includes("US") || selectedCountry.includes("CA");
      return (
        <Menu {...commonMenuProps}>
          {renderSearchField("Search country or state/province")}
          <MenuItem selected={!selectedCountry.length && !selectedState.length} onClick={() => { setSelectedCountry([]); setSelectedState([]); }}>
            All Locations
          </MenuItem>
          <Typography sx={FILTER_SECTION_LABEL_SX}>Country</Typography>
          {filteredCountryOptions.map((o) => renderOptionItem({
            key: `country-${o}`,
            label: countryLabel(o),
            checked: selectedCountry.includes(o),
            onClick: () => {
              const next = toggleStringValue(selectedCountry, o);
              setSelectedCountry(next);
              if (!next.includes("US") && !next.includes("CA")) setSelectedState([]);
            },
          }))}
          {northAmericaSelected && (
            <>
              <Typography sx={FILTER_SECTION_LABEL_SX}>State / Province</Typography>
              {filteredStateOptions.length ? (
                filteredStateOptions.map((o) => renderOptionItem({
                  key: `state-${o.code}`,
                  label: `${o.label} (${o.group})`,
                  checked: selectedState.includes(o.code),
                  onClick: () => setSelectedState(toggleStringValue(selectedState, o.code)),
                }))
              ) : (
                <MenuItem disabled>No states or provinces</MenuItem>
              )}
            </>
          )}
        </Menu>
      );
    }

    if (activeFilter === "industry") {
      return (
        <Menu {...commonMenuProps}>
          {renderSearchField("Search industries")}
          <MenuItem selected={!selectedIndustry.length} onClick={() => setSelectedIndustry([])}>
            All Industries
          </MenuItem>
          {filteredIndustryOptions.map((o) => renderOptionItem({
            key: o,
            label: o,
            checked: selectedIndustry.includes(o),
            onClick: () => setSelectedIndustry(toggleStringValue(selectedIndustry, o)),
          }))}
        </Menu>
      );
    }

    return null;
  };

  /* =========================
     FILTERING
  ========================= */

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const rowIndustry = r.new_industry || r.industry || "";
      const rowCountryCode = r.country_code || normalizeTradeShowCountryCode(r.country);
      const rowState = (r.state || "").trim().toUpperCase();

      if (q) {
        const haystack = [r.name, r.city, r.state, r.country, r.new_country, r.industry, r.new_industry, r.website, r.attendees, r.exhibitors, r.new_attendees, r.new_exhibitors]
          .filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      if (selectedCountry.length > 0 && !selectedCountry.includes(rowCountryCode)) return false;
      if (selectedState.length > 0 && !selectedState.includes(rowState)) return false;
      if (selectedIndustry.length > 0 && !selectedIndustry.includes(rowIndustry)) return false;

      if (selectedMonth.length > 0 || selectedYear.length > 0) {
        const raw = r.start_date_raw || r.start_date;
        const d = new Date(raw);
        if (isNaN(d)) return false;
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = String(d.getFullYear());
        if (selectedMonth.length > 0 && !selectedMonth.includes(month)) return false;
        if (selectedYear.length > 0 && !selectedYear.includes(year)) return false;
      }

      return true;
    });
  }, [rows, search, selectedCountry, selectedState, selectedIndustry, selectedMonth, selectedYear]);

  useEffect(() => {
    setPage(1);
  }, [search, selectedCountry, selectedState, selectedIndustry, selectedMonth, selectedYear]);

  /* =========================
     PAGINATION
  ========================= */

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const paginatedRows = filteredRows.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);

  /* =========================
     SELECTION
  ========================= */

  const currentPageIds = paginatedRows.map((r) => r.dynamics_id).filter(Boolean);
  const allCurrentSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.has(id));
  const someCurrentSelected = currentPageIds.some((id) => selectedIds.has(id));

  const toggleOne = (dynamicsId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(dynamicsId)) next.delete(dynamicsId);
      else next.add(dynamicsId);
      return next;
    });
  };

  const togglePage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allCurrentSelected) currentPageIds.forEach((id) => next.delete(id));
      else currentPageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  /* =========================
     DELETE / EXPORT
  ========================= */

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      await api.delete("/user-trade-shows", { data: { dynamics_ids: Array.from(selectedIds) } });
      await fetchTradeShows();
    } catch {
      setError("Failed to delete trade shows");
    }
  };

  const handleExport = () => {
    const exportRows = selectedIds.size > 0
      ? filteredRows.filter((r) => selectedIds.has(r.dynamics_id))
      : filteredRows;
    if (!exportRows.length) return;

    const headers = ["Name", "Start Date", "End Date", "City", "State", "Country", "Industry", "Attendees", "Exhibitors", "Website"];
    const rowsData = exportRows.map((r) => [
      r.name || "", r.start_date || "", r.end_date || "",
      r.city || "", r.state || "", r.country || r.new_country || "",
      r.industry || r.new_industry || "",
      r.attendees || r.new_attendees || "",
      r.exhibitors || r.new_exhibitors || "",
      normalizeTradeShowUrl(r.website) || "",
    ]);

    const csvContent = [headers, ...rowsData]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const today = new Date().toISOString().split("T")[0];
    downloadCsvFile(csvContent, `my_trade_shows_${today}.csv`);
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      {/* Sticky Header */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 5, borderBottom: "1px solid #dce4ef", background: "#ffffff" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: { xs: 2, md: 3 }, py: 1.75, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
            <BackButton to="/tradeshows" label="Back" />
            <Box sx={{ width: 32, height: 32, borderRadius: 2, background: BRAND_GREEN, color: "#ffffff", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
              <BookmarkIcon sx={{ fontSize: 19 }} />
            </Box>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: BRAND_BLUE, letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>
              My Trade Shows
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0, width: { xs: "100%", md: "auto" } }}>
            <SearchBar value={search} onChange={setSearch} placeholder="Search trade shows..." />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(e) => setActionsAnchorEl(e.currentTarget)}
              sx={ACTIONS_HEADER_BTN_SX}
            >
              Actions
            </Button>
            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsMenuOpen}
              onClose={() => setActionsAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
            >
              <MenuItem
                onClick={() => { setActionsAnchorEl(null); handleExport(); }}
                disabled={filteredRows.length === 0}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "#0284C7" }}>
                  <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => { setActionsAnchorEl(null); handleDeleteSelected(); }}
                disabled={selectedIds.size === 0}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "#dc2626" }}>
                  <DeleteForeverIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText sx={{ "& .MuiListItemText-primary": { color: "#dc2626" } }}>Delete Selected</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {loading && <LinearProgress />}
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component="div" sx={{ ...CARD_SX, background: "#00336C", overflow: "hidden", width: "100%", overflowX: "auto" }}>
          <Table size="small" stickyHeader sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" align="center" sx={HEAD_CELL_SX}>
                  <Checkbox
                    indeterminate={someCurrentSelected && !allCurrentSelected}
                    checked={currentPageIds.length > 0 && allCurrentSelected}
                    onChange={togglePage}
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    indeterminateIcon={<RemoveCircleIcon />}
                    sx={{ color: "rgba(255,255,255,0.6)", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#ffffff" } }}
                  />
                </TableCell>
                <TableCell sx={HEAD_CELL_SX}>Trade Show</TableCell>
                <TableCell align="center" sx={HEAD_CELL_SX}>
                  {renderFilterTrigger("Dates", "dates", selectedMonth.length > 0 || selectedYear.length > 0, 116)}
                </TableCell>
                <TableCell align="center" sx={HEAD_CELL_SX}>
                  {renderFilterTrigger("Location", "location", selectedCountry.length > 0 || selectedState.length > 0, 132)}
                </TableCell>
                <TableCell align="center" sx={HEAD_CELL_SX}>
                  {renderFilterTrigger("Industry", "industry", selectedIndustry.length > 0, 128)}
                </TableCell>
                <TableCell align="center" sx={HEAD_CELL_SX}>Attendees</TableCell>
                <TableCell align="center" sx={HEAD_CELL_SX}>Exhibitors</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRows.map((r) => {
                const safeUrl = normalizeTradeShowUrl(r.website);
                const checked = selectedIds.has(r.dynamics_id);
                const attendees = r.attendees ?? r.new_attendees;
                const exhibitors = r.exhibitors ?? r.new_exhibitors;

                return (
                  <TableRow
                    key={r.id || r.dynamics_id}
                    hover
                    sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "rgba(0,51,108,0.03) !important" } }}
                  >
                    <TableCell padding="checkbox" align="center" sx={{ borderBottom: "1px solid #edf0f5" }}>
                      <Checkbox checked={checked} onChange={() => toggleOne(r.dynamics_id)} size="small" icon={<RadioButtonUncheckedIcon />} checkedIcon={<CheckCircleIcon />} />
                    </TableCell>

                    <TableCell sx={{ ...DATA_CELL_SX, fontWeight: 700, color: BRAND_BLUE, pl: 4 }}>
                      {safeUrl ? (
                        <Typography component="a" href={safeUrl} target="_blank" rel="noopener noreferrer"
                          sx={{ fontWeight: 700, fontSize: "0.9rem", color: BRAND_BLUE, textDecoration: "none", "&:hover": { textDecoration: "underline", color: BRAND_GREEN } }}>
                          {r.name}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: BRAND_BLUE }}>{r.name}</Typography>
                      )}
                    </TableCell>

                    <TableCell align="center" sx={DATA_CELL_SX}>
                      {r.start_date || r.end_date ? `${r.start_date || "—"} – ${r.end_date || "—"}` : "—"}
                    </TableCell>

                    <TableCell align="center" sx={DATA_CELL_SX}>
                      {[r.city, r.state, r.country].filter(Boolean).join(", ") || "—"}
                    </TableCell>

                    <TableCell align="center" sx={DATA_CELL_SX}>
                      {r.industry || r.new_industry ? (
                        <Chip label={r.industry || r.new_industry} size="small"
                          sx={{ fontWeight: 600, bgcolor: BRAND_BLUE, color: "#fff", borderRadius: 1.5, maxWidth: 220, "& .MuiChip-label": { px: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }} />
                      ) : "—"}
                    </TableCell>

                    <TableCell align="center" sx={DATA_CELL_SX}>{displayCount(attendees)}</TableCell>
                    <TableCell align="center" sx={DATA_CELL_SX}>{displayCount(exhibitors)}</TableCell>
                  </TableRow>
                );
              })}

              {!loading && filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: "rgba(0,0,0,0.35)", fontSize: "0.875rem", fontWeight: 500 }}>
                    No saved trade shows.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {renderFilterMenu()}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <BrandPagination page={safePage} count={totalPages} onChange={(_e, value) => setPage(value)} />
        </Box>
      </Box>
    </Box>
  );
}
